import { getFormProps, getInputProps, useForm } from '@conform-to/react'
import { getZodConstraint, parseWithZod } from '@conform-to/zod'
import {
	json,
	type ActionFunctionArgs,
	type LoaderFunctionArgs,
} from '@remix-run/node'
import { Form, useActionData, useLoaderData } from '@remix-run/react'
import { HoneypotInputs } from 'remix-utils/honeypot/react'
import { z } from 'zod'
import { CheckboxField, ErrorList, Field } from '#app/components/forms.tsx'
import { StatusButton } from '#app/components/ui/status-button.tsx'
import { requireAnonymous } from '#app/utils/auth.server.ts'
import { prisma } from '#app/utils/db.server.ts'
import { checkHoneypot } from '#app/utils/honeypot.server.ts'
import { useIsPending } from '#app/utils/misc.tsx'
import { EmailSchema, NameSchema } from '#app/utils/user-validation.ts'

const MailListFormSchema = z
	.object({
		first_name: NameSchema,
		last_name: NameSchema.optional(),
		email: EmailSchema,
		teacher: z.boolean().optional(),
		student: z.boolean().optional(),
		parent: z.boolean().optional(),
	})
	.superRefine((data, ctx) => {
		if (!(data.teacher || data.student || data.parent)) {
			ctx.addIssue({
				code: z.ZodIssueCode.custom,
				message: `At least one usage interest is required.`,
			})
		}

		if (data.teacher) {
			if (data.student) {
				ctx.addIssue({
					code: z.ZodIssueCode.custom,
					message: `Tremolo doesn't allow members to be both a teacher and a student.`,
				})
			}

			if (data.parent) {
				ctx.addIssue({
					code: z.ZodIssueCode.custom,
					message: `Tremolo doesn't allow members to be both a teacher and a parent.`,
				})
			}
		}
	})

export async function loader({ request }: LoaderFunctionArgs) {
	await requireAnonymous(request)
	const requestUrl = new URL(request.url)
	const isLucy = requestUrl.searchParams.get('lucy') === 'tremoloboss'

	if (isLucy) {
		const signups = await prisma.mailListContact.findMany({
			orderBy: { createdAt: 'desc' },
		})

		return json({ signups })
	}

	return json({ signups: null })
}

export async function action({ request }: ActionFunctionArgs) {
	await requireAnonymous(request)
	const formData = await request.formData()
	checkHoneypot(formData)
	const submission = await parseWithZod(formData, {
		schema: (intent) =>
			MailListFormSchema.transform(async (data, ctx) => {
				if (intent !== null) return { ...data }

				try {
					await prisma.mailListContact.create({
						data: {
							email: data.email,
							first_name: data.first_name,
							last_name: data.last_name,
							teacher: data.teacher,
							student: data.student,
							parent: data.parent,
						},
					})
					// eslint-disable-next-line @typescript-eslint/no-unused-vars
				} catch (error) {
					ctx.addIssue({
						code: z.ZodIssueCode.custom,
						message: 'You have already joined our mailing list - thanks!',
					})

					return z.NEVER
				}

				try {
					let message = `${data.first_name} just submitted the Tremolo mailing list form!`

					let interests = [
						data.teacher ? 'usage as a teacher' : null,
						data.student ? 'usage as a student' : null,
						data.parent ? 'usage as a parent' : null,
					].filter(Boolean)
					let interestList = new Intl.ListFormat().format(interests)

					if (interestList.length) {
						message = `${message} They're interested in ${interestList}!`
					}

					let ntfyUrl = 'https://ntfy.sh/public-tremolo-signup-log'
					if (ENV.MODE === 'development') {
						ntfyUrl = `${ntfyUrl}-test`
					}

					fetch(ntfyUrl, {
						method: 'POST',
						body: message,
						headers: { Title: 'Tremolo Mail List Submission' },
					}).catch(() => {})
					// eslint-disable-next-line @typescript-eslint/no-unused-vars
				} catch (e) {}

				return { ...data }
			}),
		async: true,
	})

	return json(
		{ result: submission.reply() },
		{ status: submission.status === 'error' ? 400 : 200 },
	)
}

export default function MailListRoute() {
	const loaderData = useLoaderData<typeof loader>()
	const actionData = useActionData<typeof action>()
	const isPending = useIsPending()

	const [form, fields] = useForm({
		id: 'mail-list-form',
		constraint: getZodConstraint(MailListFormSchema),
		defaultValue: {},
		lastResult: actionData?.result,
		onValidate({ formData }) {
			return parseWithZod(formData, { schema: MailListFormSchema })
		},
		shouldRevalidate: 'onBlur',
	})

	const success = actionData?.result.status === 'success'

	if (loaderData.signups) {
		return (
			<div className="mx-auto grid max-w-screen-md gap-y-6 px-4 pt-20">
				<div>
					<b>Total Count:</b> {loaderData.signups.length}
				</div>
				{loaderData.signups.map((s) => {
					return (
						<div key={s.id}>
							<b>First:</b> {s.first_name} | <b>Last:</b> {s.last_name} |{' '}
							<b>email:</b> {s.email} | <b>teach:</b> {s.teacher ? 'yes' : 'no'}{' '}
							| <b>student:</b> {s.student ? 'yes' : 'no'} | <b>parent:</b>{' '}
							{s.parent ? 'yes' : 'no'} | <b>When:</b> {s.createdAt}
						</div>
					)
				})}
			</div>
		)
	}

	return (
		<div className="flex min-h-full flex-col items-center justify-start overflow-clip bg-primary-light pb-32 pt-12">
			<div className="relative mx-auto w-full max-w-xs sm:max-w-xl">
				<div className="pointer-events-none absolute -left-[15rem] top-[5rem] h-44 w-64 select-none rounded-[60px] bg-secondary opacity-20 sm:-left-[18rem] sm:top-32 sm:h-24" />
				<div className="pointer-events-none absolute -left-[19rem] top-[18rem] h-[18rem] w-[20rem] select-none rounded-[60px] bg-gradient-to-b from-primary to-primary-dark opacity-20 sm:-left-[22rem] sm:top-[17rem]" />
				<div className="pointer-events-none absolute -left-[17rem] top-[38rem] h-[18rem] w-[18rem] select-none rounded-[60px] bg-primary opacity-20 sm:-left-[20rem] sm:top-[38rem]" />
				<div className="pointer-events-none absolute -right-[17rem] top-[10rem] h-[18rem] w-[18rem] select-none rounded-[60px] bg-gradient-to-b from-primary to-primary-dark opacity-20 sm:-right-[20rem] sm:top-[10rem]" />
				<div className="pointer-events-none absolute -right-[19rem] top-[31rem] h-[20rem] w-[20rem] select-none rounded-[60px] bg-primary opacity-20" />

				{success ? (
					<h1 className="px-4 pt-20 text-center font-display text-h2">
						Got it, you're all set!
					</h1>
				) : null}

				<div>
					<div className="mx-auto w-full px-8">
						{success ? null : (
							<Form method="POST" {...getFormProps(form)}>
								<HoneypotInputs />
								<div className="flex flex-col gap-x-4 sm:flex-row">
									<Field
										labelProps={{
											children: 'First Name',
											className:
												'bg-gradient-to-b from-primary via-primary-dark via-30% to-primary-dark bg-clip-text font-bold text-transparent',
										}}
										inputProps={{
											...getInputProps(fields.first_name, { type: 'text' }),
											autoFocus: true,
											autoComplete: 'given-name',
											className: 'rounded-none',
										}}
										className="flex-1"
										errors={fields.first_name.errors}
									/>
									<Field
										labelProps={{
											children: 'Last Name',
											className:
												'bg-gradient-to-b from-primary via-primary-dark via-30% to-primary-dark bg-clip-text font-bold text-transparent',
										}}
										inputProps={{
											...getInputProps(fields.last_name, { type: 'text' }),
											autoComplete: 'family-name',
											className: 'rounded-none',
										}}
										className="flex-1"
										errors={fields.last_name.errors}
									/>
								</div>

								<div>
									<Field
										labelProps={{
											children: 'Email',
											className:
												'bg-gradient-to-b from-primary via-primary-dark via-30% to-primary-dark bg-clip-text font-bold text-transparent',
										}}
										inputProps={{
											...getInputProps(fields.email, { type: 'email' }),
											className: 'lowercase rounded-none',
											autoComplete: 'email',
										}}
										errors={fields.email.errors}
									/>
								</div>

								<p className="bg-gradient-to-b from-primary via-primary-dark via-30% to-primary-dark bg-clip-text pb-4 text-center font-bold text-transparent">
									I'm interested in using Tremolo as:
								</p>

								<div className="mx-auto flex flex-wrap justify-center gap-x-4">
									<CheckboxField
										labelProps={{
											htmlFor: fields.teacher.id,
											children: 'an instructor',
										}}
										buttonProps={getInputProps(fields.teacher, {
											type: 'checkbox',
										})}
										errors={fields.teacher.errors}
									/>
									<CheckboxField
										labelProps={{
											htmlFor: fields.student.id,
											children: 'a student',
										}}
										buttonProps={getInputProps(fields.student, {
											type: 'checkbox',
										})}
										errors={fields.student.errors}
									/>
									<CheckboxField
										labelProps={{
											htmlFor: fields.parent.id,
											children: 'a parent',
										}}
										buttonProps={getInputProps(fields.parent, {
											type: 'checkbox',
										})}
										errors={fields.parent.errors}
									/>
								</div>

								<div className="text-center">
									<ErrorList errors={form.errors} id={form.errorId} />
								</div>

								<div className="flex items-center justify-center gap-6 pt-3">
									<StatusButton
										className="w-1/2 rounded-none bg-zinc-500 font-bold hover:bg-zinc-600 focus-visible:bg-zinc-600 active:bg-zinc-600"
										status={isPending ? 'pending' : (form.status ?? 'idle')}
										type="submit"
										disabled={isPending}
									>
										Submit
									</StatusButton>
								</div>
							</Form>
						)}
					</div>
				</div>
			</div>
		</div>
	)
}
